import type { AccountChat } from "@/lib/accountChatsParser"
import { parse as jidParse } from "@/lib/cachedJid"
import type { Message } from "@/reducers/messagesSlice"

export type TransicsMessagePresentation = {
  authorName: string
  body: string
  bodyOffset: number
}

const TRANSICS_DRIVER_GROUP = "transics_driver"
const TRANSICS_AUTHOR_PREFIX = /^\*([^*\r\n]{1,100})\*:[ \t]*(?:\r?\n)?/
const REPEATED_AUTHOR_PREFIX = /^([^:\r\n]{1,100}):[ \t]*(?:\r?\n)?/

const normalizedNameParts = (name: string): string[] =>
  name
    .normalize("NFC")
    .trim()
    .split(/\s+/)
    .map((part) => part.toLocaleLowerCase("pl-PL"))
    .sort((left, right) => left.localeCompare(right, "pl-PL"))

const containsTheSameName = (left: string, right: string): boolean => {
  const leftParts = normalizedNameParts(left)
  const rightParts = normalizedNameParts(right)

  return (
    leftParts.length === rightParts.length &&
    leftParts.every((part, index) => part === rightParts[index])
  )
}

const removeRepeatedAuthorPrefix = (
  body: string,
  authorName: string,
): { body: string; offset: number } => {
  const match = REPEATED_AUTHOR_PREFIX.exec(body)
  if (!match || !containsTheSameName(match[1], authorName)) {
    return { body, offset: 0 }
  }

  return {
    body: body.slice(match[0].length),
    offset: match[0].length,
  }
}

/**
 * Transics encodes the real sender in the text as `*NAME*:`. Keep this parser
 * deliberately strict: ordinary chat messages may legitimately begin with
 * Markdown-like text and must not be changed.
 */
export const parseTransicsMessageBody = (
  body: string,
): TransicsMessagePresentation | undefined => {
  const match = TRANSICS_AUTHOR_PREFIX.exec(body)
  if (!match) return undefined

  const authorName = match[1].trim()
  if (!authorName) return undefined

  const bodyWithoutAuthor = body.slice(match[0].length)
  const withoutRepeatedAuthor = removeRepeatedAuthorPrefix(
    bodyWithoutAuthor,
    authorName,
  )

  return {
    authorName,
    body: withoutRepeatedAuthor.body,
    bodyOffset: match[0].length + withoutRepeatedAuthor.offset,
  }
}

export const getTransicsMessagePresentation = (
  message: Pick<Message, "body" | "from">,
  accountChat?: AccountChat,
): TransicsMessagePresentation | undefined => {
  if (accountChat?.group !== TRANSICS_DRIVER_GROUP) return undefined

  try {
    if (jidParse(message.from).resource !== "forwarder") return undefined
  } catch {
    return undefined
  }

  return parseTransicsMessageBody(message.body)
}
